/**
 * Motor de gravação de voz do composer — vive FORA de qualquer instância de
 * `SendMessage`.
 *
 * ★ Por quê: `view-chat.vue` tem DUAS instâncias mutuamente exclusivas do
 * composer — a do herói (`composerHeroi`, tela vazia) e a do rodapé
 * (`composerRodape`, conversa em andamento) — alternadas por `v-if`/`v-else`
 * (`composerNoHeroi`). Trocar de uma pra outra DESTRÓI a instância antiga e
 * MONTA uma nova. [feedback do usuário, 04/09] "ao clicar no iniciar modo de
 * voz, deveria iniciar a conversa... expandir mesmo como se eu tivesse
 * mandando mensagem" — isto exige que `composerNoHeroi` mude assim que o
 * Modo de Voz começa, e não só quando a mensagem transcrita é enviada. Se o
 * `GravadorDeTurno`/Web Speech API vivessem dentro do componente (como
 * viviam antes), a troca destruiria a gravação NO MEIO dela.
 *
 * Reativo via um `Vue` isolado — mesmo padrão do `GlobalBus`
 * (`@services/bus`): qualquer componente que leia `motorDeVoz.gravando` (e
 * os outros campos reativos) reage à mudança automaticamente, inclusive uma
 * instância de `SendMessage` recém-montada — ela "encontra" a gravação já em
 * andamento, sem reiniciar nada.
 *
 * ## Voz v3 (08/09) — split de arquivo e três feedbacks do owner
 *
 * Este arquivo passou do teto de 500 linhas ao ganhar o barge-in — três
 * peças saíram pra módulos irmãos, todos reexportados daqui pros
 * consumidores (`send-message.vue`, `view-chat.vue`) não mudarem de import:
 *
 * | peça | onde |
 * |---|---|
 * | a instância reativa `motorDeVoz` (só o `data()`, sem lógica) | `estado-da-voz.js` |
 * | VAD de auto-envio + VAD de barge-in (constantes e factories) | `vad-de-voz.js` |
 * | fila de fala, `interromperFalaDaIa`, `iniciarFalaProgressiva`/`processarDeltaDeFala`/`processarEventoFala`/`finalizarFalaProgressiva`, `reproduzirResposta` | `fala-da-ia.js` |
 * | [G-4 SHOULD, review spec-019, 10/09] o observador de silêncio terminal (US-5) — factory pura, sem tocar `motorDeVoz` | `observador-de-silencio.js` |
 * | [spec-008, T002] a camada impura de medição (relógio + índice de fala reconhecimento/conteúdo) que liga o núcleo `medicao-de-voz.js` ([T001]) ao caminho por turnos — desligada por padrão (só o roteiro golden liga) | `instrumentacao-de-medicao.js` |
 *
 * ## Voz v4 (spec-008-voz-conversacional-tempo-real-chat, [T020]-[T022]) —
 * o transporte por turnos virou módulo próprio
 *
 * Este arquivo deixou de SER o transporte — a maquinaria inteira (pedido de
 * microfone/gravação, VAD, barge-in local, STT, silêncio terminal) mudou de
 * casa pra `transporte-por-turnos.js`, que implementa `TransporteDeVoz`
 * (`contracts/transporte.md`). Este arquivo virou uma FACHADA FINA: cada
 * função pública abaixo (`iniciar`/`parar`/`cancelar`/`alternarMudo`/
 * `sairDoModoDeVoz`/`registrarDestino`/`desregistrarDestino`) só pede o
 * transporte certo a `transporte-de-voz.js::selecionarTransporte` (o ÚNICO
 * ponto de seleção do sistema, invariante N4) e repassa os argumentos —
 * MESMA assinatura, MESMO comportamento observável de antes ([T022], "API
 * pública idêntica"). Quando o transporte `tempoReal` existir ([T030]), é
 * `transporte-de-voz.js` quem muda — nenhum consumidor deste arquivo
 * (`send-message.vue`, `view-chat.vue`, `api/apiChat.js`) precisa saber.
 *
 * `motorDeVoz` (o estado reativo), a fila de fala (`fala-da-ia.js`) e a
 * medição (`instrumentacao-de-medicao.js`) continuam SEM dono de transporte
 * — são infraestrutura compartilhada que qualquer transporte lê/escreve, daí
 * seguirem importadas/reexportadas direto daqui, sem passar pela seleção.
 *
 * ## Voz v4, parte 2 ([T030b]) — a fachada LIGA o tempo real e fala o
 * vocabulário do CONTRATO
 *
 * Três mudanças, e a bandeira desligada não vê nenhuma delas:
 *
 * | o quê | por quê |
 * |---|---|
 * | `import './transporte-tempo-real'` | é o que faz o registro no seletor acontecer; sem ninguém carregar o módulo, o Modo de Voz cai em `porTurnos` do mesmo jeito |
 * | abrir por `abrir(ctx)` (contrato) em vez de `iniciar(modo, avisos)` (v1) | o tempo real não tem `iniciar`; o `porTurnos` tem os dois desde a [T020] |
 * | as seis operações mode-agnósticas perguntam `transporte-no-comando.js` | resolve o ponto de extensão que a [T022] deixou nomeado: elas precisam do transporte que a SESSÃO abriu, não de uma seleção nova |
 *
 * ## Voz v4, parte 3 ([T030c]) — a TELA passou a consumir o `Destino`
 *
 * O que a [T030b] deixou declarado como pendente está feito: `send-message.vue`
 * espalha sobre o destino do v1 os oito eventos do contrato em tempo real
 * (`utils/destino-de-voz-tempo-real.js`), anexa o `MediaStream` do
 * `audioRemoto` a um `<audio>` próprio e produz o `ContextoDeSessao`
 * (`utils/contexto-de-voz-tempo-real.js`: a `superficie` declarada pela tela e
 * o `forcarPorTurnos` da queda). Sem aquilo, a sessão em tempo real era paga e
 * MUDA.
 *
 * ★ **[T046] O tempo real é o PADRÃO** (D-O-4): a bandeira por escritório saiu e
 * a D-9 foi revogada. Quem não declara `superficie` (popup, painel do Hub,
 * Fee-Based, ai-framework) mantém o caminho por turnos byte a byte — não por
 * rollout, mas porque `abrir` recusa sessão sem superfície.
 *
 * ## Medição (spec-008-voz-conversacional-tempo-real-chat [T002])
 *
 * Instrumentação OPCIONAL do caminho por turnos com as marcas do núcleo
 * (`medicao-de-voz.js`, [T001]) — desligada em produção, ligada só pelo
 * roteiro golden (US-1 AC2). A lógica (relógio injetável, índice de fala
 * reconhecimento×conteúdo, o gate de `houveTool`) mora em
 * `instrumentacao-de-medicao.js`; os QUATRO pontos de chamada
 * (`marcarFimDaFala()`/`marcarInicioDeFala()`/`marcarFimDeFalaAtual()`/
 * `marcarFalhaDaMedicao()`) migraram para `transporte-por-turnos.js` junto
 * com o resto da maquinaria do caminho por turnos.
 */
import { MODO_DITAR, MODO_VOZ } from './captura-de-voz'
import { motorDeVoz } from './estado-da-voz'
import {
  abrirTransporte,
  selecionarTransporte,
  MENSAGEM_QUEDA_PARA_POR_TURNOS,
} from './transporte-de-voz'
import {
  definirTransporteNoComando,
  porTurnosNoComando,
  transporteNoComando,
} from './transporte-no-comando'
// ★ [spec-008 T030b] É ESTA LINHA que liga o transporte em tempo real. O
// módulo se registra no ponto único de seleção ao ser carregado
// (`registrarTransporteTempoReal`, no rodapé dele) e, sem alguém carregá-lo,
// `selecionarTransporte` caía em `porTurnos` mesmo com a bandeira ligada.
//
// ⚠️ O import mora AQUI e **nunca** em `transporte-de-voz.js`, que
// `transporte-tempo-real.js` importa: seria ciclo, e o registro cairia em
// zona morta no meio da carga (o `export const registrarTransporteTempoReal`
// ainda não existiria quando o rodapé dele rodasse).
//
// Carregar ≠ usar: no Ditar, na queda do [T034] e em composer sem
// `superficie` declarada, a seleção continua em `porTurnos` byte a byte
// ([T046], D-O-4).
import './transporte-tempo-real'
import {
  iniciarFalaProgressiva,
  processarDeltaDeFala,
  processarEventoFala,
  finalizarFalaProgressiva,
  reproduzirResposta,
} from './fala-da-ia'
import { LIMIAR_MS_OBSERVADOR_DE_SILENCIO } from './observador-de-silencio'
import {
  iniciarMedicaoDoTurno,
  encerrarMedicaoDoTurno,
  medicaoDoTurnoAtiva,
} from './instrumentacao-de-medicao'

export { MODO_DITAR, MODO_VOZ, motorDeVoz }
// ★ [T030c] A copy do [T034] reexportada pela FACHADA. O composer diz essa
// mesma linha quando o canal cai no MEIO da sessão (evento `queda` do
// `Destino`) — a mensagem tem de ser UMA, e importar `transporte-de-voz.js`
// de dentro de um `.vue` furaria a regra que esta fachada existe para manter:
// nenhum componente conhece o ponto de seleção de transporte (US-3 AC3).
export { MENSAGEM_QUEDA_PARA_POR_TURNOS }
export {
  iniciarFalaProgressiva,
  processarDeltaDeFala,
  processarEventoFala,
  finalizarFalaProgressiva,
  reproduzirResposta,
  LIMIAR_MS_OBSERVADOR_DE_SILENCIO,
}
// [spec-008 T002] API pública da medição — ver `instrumentacao-de-medicao.js`.
export { iniciarMedicaoDoTurno, encerrarMedicaoDoTurno, medicaoDoTurnoAtiva }

/**
 * [T022] `parar`/`cancelar`/`alternarMudo`/`sairDoModoDeVoz`/
 * `registrarDestino`/`desregistrarDestino` operam sobre "o transporte com o
 * microfone aberto agora" — chamadas sem saber de antemão qual `modo` está
 * em curso (podem vir tanto durante Ditar quanto durante Modo de Voz).
 *
 * ★ [T030b] O ponto de extensão que a [T022] deixou NOMEADO está resolvido:
 * em vez de re-selecionar do zero a cada chamada (o truque do `MODO_DITAR`,
 * que só era correto enquanto `porTurnos` era o único transporte que
 * existia), estas seis operações perguntam QUEM ABRIU a sessão corrente —
 * `transporte-no-comando.js`. Sem sessão aberta, a resposta é `porTurnos`,
 * que é exatamente o que a linha antiga devolvia: o comportamento de hoje
 * continua byte a byte.
 */
const _transporteDaSessaoAtual = () =>
  transporteNoComando() || selecionarTransporte({ modo: MODO_DITAR })

/**
 * ★ [T030b] Devolve o comando ao caminho por turnos e apaga o que a FACHADA
 * escreveu na tela pela sessão em tempo real.
 *
 * A ORDEM importa: `sessaoDeVozAtiva` cai ANTES do comando voltar, senão a
 * borda `true → false` dispararia o `$watch` do v1 (que pararia um
 * observador de silêncio nunca iniciado). No caminho por turnos isto é um
 * no-op puro — ele nunca sai do comando, e é ele mesmo quem zera o campo.
 */
const _devolverComandoAoPorTurnos = () => {
  if (porTurnosNoComando()) return
  motorDeVoz.sessaoDeVozAtiva = false
  definirTransporteNoComando(null)
}

/**
 * Pede o microfone e arranca a gravação do turno.
 *
 * ★ [T030b] Duas mudanças, as duas para o transporte em tempo real caber
 * atrás da MESMA fachada — e nenhuma delas visível com a bandeira desligada:
 *
 * 1. **Abre pelo verbo do CONTRATO (`abrir`), não pelo `iniciar` do v1.**
 *    `transporte-tempo-real.js` implementa `TransporteDeVoz` e não tem
 *    `iniciar`; o `porTurnos` expõe `abrir(ctx)` como alias EXATO de
 *    `iniciar(ctx.modo, ctx.avisos)` desde a [T020]. Um `if` por método na
 *    fachada era justamente o que a D-2 mandou evitar.
 * 2. **Quem abre é `abrirTransporte`** (`transporte-de-voz.js`), que é o
 *    ponto único de seleção — e é lá que mora a queda para o caminho por
 *    turnos quando o tempo real não abre ([T034]).
 *
 * @param {string} modo `MODO_VOZ` | `MODO_DITAR`.
 * @param {Object} avisos `{avisarSemSuporte, avisarErro}` — os canais de uma
 *   linha da tela; a queda do [T034] também fala por `avisarErro`.
 * @param {Object} [contexto] o `ContextoDeSessao` de
 *   `contracts/transporte.md` — `superficie`, `delegar`, `idConversa`,
 *   `forcarPorTurnos`. **Terceiro parâmetro, aditivo**: chamada sem ele (ou
 *   sem `superficie`) cai em `porTurnos`, como sempre.
 * @returns {Promise<{transporte: Object, caiu: boolean, motivo: string|null}>}
 */
export const iniciar = async (modo, avisos, contexto) => {
  const desfecho = await abrirTransporte({
    ...(contexto || {}),
    modo,
    avisos,
    // ★ [T050] O destino da TELA viaja com o contexto para que o ponto único
    // de seleção possa entregá-lo a quem assumir o microfone. Ver
    // `registrarDestino` abaixo para o defeito que isto corrige.
    destino: _destinoRegistrado,
  })
  // ★ [T030b] "Sem abrir sessão sem UI": `view-chat.vue`/`send-message.vue`
  // decidem o que desenhar por `sessaoDeVozAtiva`, e o transporte em tempo
  // real não escreve esse campo de propósito (escrevê-lo LÁ armaria os
  // `$watch` do v1 — ver `transporte-no-comando.js`). Aqui é seguro: com o
  // comando fora do `porTurnos` os dois watchers já estão calados, e a
  // fachada é a camada que sabe que existe sessão para a tela desenhar.
  //
  // O `porTurnos` escreve o campo sozinho, e SÓ no `MODO_VOZ` — por isso a
  // fachada não encosta nele quando o comando é dele: escrever aqui ligaria
  // a UI de voz também no Ditar.
  if (!porTurnosNoComando()) motorDeVoz.sessaoDeVozAtiva = true
  return desfecho
}

/**
 * Para a gravação, transcreve/encerra o turno e distribui o resultado.
 * `manual` distingue quem pediu a parada — ver `transporte-por-turnos.js`.
 *
 * ⚠️ [T030b] `parar` é vocabulário do v1 e **não existe no contrato**: quem
 * julga o fim do turno em tempo real é o provedor (N4), e o protocolo MEDIDO
 * não tem evento cliente→servidor de "acabei de falar" — forjar um seria
 * protocolo não medido, que é o erro que esta spec já cometeu uma vez. Nada
 * disso mudou: **nenhum evento novo vai ao fio aqui.**
 *
 * ★ [T049] O que mudou é o DESFECHO quando o transporte não tem `parar`: em
 * vez de um no-op, **encerra a sessão** — o mesmo tratamento que o `Esc`
 * (`cancelar`) já recebia desde a [T030b], e pelo mesmo critério dela: *tecla
 * morta sobre sessão PAGA é pior que qualquer alternativa*.
 *
 * O no-op foi declarado como divergência e ninguém o ligou ao BOTÃO. Numa
 * sessão em tempo real `motorDeVoz.gravando` fica `true` do começo ao fim (o
 * microfone não fecha entre turnos), então o X neutro do composer — o único
 * ligado a `sairDoModoDeVoz` — vive sob `sessaoDeVozAtiva && !gravando` e
 * **nunca renderiza**: o X que o consultor vê e clica é o PRETO, e ele
 * chamava esta função. Resultado medido pelo owner em 30 segundos de uso: o X
 * não fechava a conversa, e ela é paga por minuto.
 *
 * A tela também foi corrigida (o botão certo, com o rótulo certo — ver
 * `paradaDeTurnoDisponivel` abaixo). Esta guarda fica de qualquer forma:
 * afordance errada volta num refactor de template; sessão paga que não fecha
 * não pode depender disso.
 */
export const parar = (opts) => {
  const transporte = _transporteDaSessaoAtual()
  if (typeof transporte.parar !== 'function') return sairDoModoDeVoz()
  return transporte.parar(opts)
}

/**
 * ★ [T049] Existe, na sessão em curso, um turno que dê para **parar e
 * enviar**? É a pergunta que o composer faz para escolher entre o X preto
 * ("Parar e enviar", do v1) e o X neutro ("Sair do Modo de Voz").
 *
 * **Isto não fura o `nome` do contrato** (*"NUNCA é lido por componente de
 * tela"*, `contracts/transporte.md`). O que se pergunta é uma CAPACIDADE da
 * própria interface — `typeof transporte.parar === 'function'` —, não a
 * identidade de quem a implementa. A diferença é prática: um terceiro
 * transporte responde certo sem ninguém tocar no composer, que é exatamente o
 * que a proibição do `nome` protege. Um `if (nome === 'tempoReal')` na tela
 * não teria essa propriedade.
 *
 * Fora de sessão a resposta é `true` (o v1 responde por todo o resto do
 * sistema de voz) — e a tela só consulta DENTRO de uma sessão.
 */
export const paradaDeTurnoDisponivel = () =>
  typeof _transporteDaSessaoAtual().parar === 'function'

/**
 * Esc CANCELA a gravação em andamento — nem transcreve, nem envia.
 *
 * [T030b] Sem gravação para descartar (tempo real), Esc ENCERRA a sessão: a
 * alternativa seria uma tecla morta sobre uma sessão PAGA em curso.
 */
export const cancelar = () => {
  const transporte = _transporteDaSessaoAtual()
  if (typeof transporte.cancelar !== 'function') return sairDoModoDeVoz()
  return transporte.cancelar()
}

/** Mudo do Modo de Voz — alterna o valor atual e aplica ao transporte. */
export const alternarMudo = () =>
  _transporteDaSessaoAtual().alternarMudo(!motorDeVoz.microfoneMutado)

/**
 * Sai do Modo de Voz de vez, sem transcrever nem enviar nada.
 *
 * [T030b] Pelo verbo do CONTRATO (`fechar`), que no `porTurnos` é alias
 * exato de `sairDoModoDeVoz` desde a [T020] e no tempo real é o único lugar
 * que manda `session.close`, solta o microfone e registra a duração
 * cobrável.
 */
export const sairDoModoDeVoz = () => {
  const transporte = _transporteDaSessaoAtual()
  _devolverComandoAoPorTurnos()
  return transporte.fechar()
}

/**
 * O `SendMessage` atualmente montado registra para onde o texto final vai —
 * `aoPreencherTexto` (Ditar → `@input`) e `aoEnviarVoz` (Modo de Voz →
 * `@enviar-voz`). Chamado no `created()`/`mounted()` de CADA instância —
 * herói e rodapé —, então o destino sempre aponta pra quem está na tela.
 *
 * ★ [T050] O destino também fica GUARDADO aqui, e o motivo é um defeito
 * MEDIDO: `mounted()` roda **antes** de existir sessão, e nesse instante
 * `_transporteDaSessaoAtual()` é o `porTurnos` (default declarado de
 * `transporte-no-comando.js`). Quando o consultor clicava no microfone e o
 * tempo real assumia, o transporte novo nascia **sem destino nenhum** — e
 * `emitir` dele é `if (!_destino …) return`. Resultado: `audioRemoto` nunca
 * chegava ao `<audio>` (sessão PAGA e MUDA, o mesmo desfecho que a [T030c]
 * corrigiu só para o caso do composer que REMONTA no meio da sessão),
 * `transcricaoParcial` não pintava e `erro`/`queda` morriam calados.
 *
 * Quem re-registra é o ponto ÚNICO de seleção (`abrirTransporte`), que é o
 * único lugar que sabe quem assumiu — e o faz ANTES do `abrir`, porque o
 * data channel começa a entregar evento assim que a negociação fecha.
 */
let _destinoRegistrado = null

export const registrarDestino = (destino) => {
  _destinoRegistrado = destino || null
  return _transporteDaSessaoAtual().registrarDestino(destino)
}

export const desregistrarDestino = (destino) => {
  // Só esquece se for o MESMO objeto: herói e rodapé se alternam e o `mounted`
  // do novo roda antes do `beforeDestroy` do antigo — esquecer aqui sem
  // comparar apagaria o destino que acabou de entrar.
  if (_destinoRegistrado === destino) _destinoRegistrado = null
  return _transporteDaSessaoAtual().desregistrarDestino(destino)
}
